/**
 * Platform console sign-in.
 *
 * Laid out like the prototype's login: a coloured, diagonally-clipped panel carrying
 * the pitch, beside a form pane.
 *
 * Its own hostname, so this page is never reachable from a tenant domain. Identity
 * comes from Authentik over OIDC; authority comes from `is_superuser` in the Open
 * edX database. The two are not synced, so signing in successfully and being
 * allowed in are separate outcomes — the copy has to make that legible when someone
 * authenticates fine and still gets refused.
 */

import { useState } from 'react';
import { Banner, Button, Field } from '../../shared/ui';
import { getConfig } from '../../shared/lib/config';
import { signIn, signInWithPassword } from '../../shared/lib/session';
import './login.css';

export default function Login({ refused = false }: { refused?: boolean }) {
  const config = getConfig();
  const [showNative, setShowNative] = useState(false);

  return (
    <main className="login">
      {/* Left panel: the prototype's coloured, diagonally-clipped pane. Its content
          states what this console reaches BEFORE you authenticate, because signing in
          here means something different from signing in to an academy — and the two
          would otherwise look identical. */}
      <aside className="login__visual">
        <div className="login__logo">Mereka</div>

        <div className="login__pitch">
          <h2>Every academy, from one place</h2>
          <p>
            Create academies, set their branding, and choose which features each one
            gets &mdash; without a deploy.
          </p>
        </div>

        <div className="login__facts">
          <div>
            <b>Scope</b>
            <span>All tenants on this platform</span>
          </div>
          <div>
            <b>Access</b>
            <span>Platform administrators only</span>
          </div>
        </div>
      </aside>

      <section className="login__form-wrap">
        <div className="login__form-card">
          <p className="login__eyebrow">Platform console</p>
          <h1 className="login__title">Sign in to manage academies</h1>
          <p className="login__sub">
            Changes made here affect every academy on the platform, so access is
            limited to platform administrators.
          </p>

          {refused && (
            <Banner tone="danger">
              <strong>Your account isn&rsquo;t a platform administrator.</strong> You
              signed in successfully, but this console needs superuser access. Ask an
              existing platform administrator to grant it.
            </Banner>
          )}

          <div className="login__actions">
            <Button variant="primary" onClick={() => signIn()}>
              Continue with single sign-on
            </Button>

            {config.auth.allowNativeLogin && !showNative && (
              <Button variant="ghost" onClick={() => setShowNative(true)}>
                Use a password instead
              </Button>
            )}
          </div>

          {showNative && <NativeLogin />}

          <p className="login__foot">
            Signing in here does not change what you can do inside an academy.
          </p>
        </div>
      </section>
    </main>
  );
}

/**
 * Fallback password sign-in.
 *
 * Kept because Open edX native login still exists and is the only way in if the
 * identity provider is down — a real risk for a console whose whole job is fixing
 * things when they break. Posts to the LMS session endpoint, not to our BFF.
 */
function NativeLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);

    try {
      await signInWithPassword(email, password);
      // Reload rather than routing: the session cookie changes what every
      // subsequent request returns, so a fresh boot is the honest way to pick it up.
      window.location.reload();
    } catch (err) {
      const status = (err as { status?: number }).status;
      setError(
        status === 400 || status === 403
          ? 'That email or username and password did not match an account.'
          : 'Sign-in is unavailable right now. Try single sign-on.',
      );
      setBusy(false);
    }
  }

  return (
    <form className="login__form" onSubmit={submit} noValidate>
      <Field
        label="Email or username"
        name="email"
        autoComplete="username"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        required
      />
      <Field
        label="Password"
        name="password"
        type="password"
        autoComplete="current-password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        required
      />
      {error && <Banner tone="danger">{error}</Banner>}
      <Button type="submit" variant="primary" busy={busy}>
        Sign in
      </Button>
    </form>
  );
}
